import MDEditor from '@uiw/react-md-editor';
import { Loader2 } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type ClipboardEvent,
} from 'react';

import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import { uploadFile, type UploadResult } from '@/lib/collab-api';
import { cn } from '@/lib/utils';

interface Props {
  value: string;
  onChange: (next: string) => void;
  orgId: string;
  issueId?: string | undefined;
  projectId?: string | undefined;
  placeholder?: string;
  className?: string;
  minHeight?: number;
}

const IMAGE_MIME = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);

export function MarkdownEditor({
  value,
  onChange,
  orgId,
  issueId,
  projectId,
  placeholder,
  className,
  minHeight = 180,
}: Props) {
  const { error } = useToast();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const dropRef = useRef<HTMLDivElement | null>(null);

  // Follow the app's current theme so the MD editor doesn't flash bright white
  // when the rest of the UI is in dark mode. `<html class="dark">` is toggled
  // by the ui-store theme effect in App.tsx; observe it so switching theme at
  // runtime updates the editor too.
  const [colorMode, setColorMode] = useState<'light' | 'dark'>(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  });
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const apply = () => {
      setColorMode(root.classList.contains('dark') ? 'dark' : 'light');
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const performUpload = useCallback(
    async (file: File) => {
      setUploading(true);
      setProgress(0);
      try {
        const result: UploadResult = await uploadFile(
          orgId,
          file,
          {
            ...(issueId ? { issueId } : {}),
            ...(projectId ? { projectId } : {}),
          },
          (pct) => setProgress(pct),
        );
        const isImage = IMAGE_MIME.has(file.type);
        const snippet = isImage
          ? `\n\n![${file.name}](/api/uploads/${result.id}?variant=display)\n`
          : `\n\n[${file.name}](/api/uploads/${result.id})\n`;
        onChange((value ?? '') + snippet);
      } catch (err) {
        error('Upload failed', extractApiErrorMessage(err));
      } finally {
        setUploading(false);
        setProgress(0);
      }
    },
    [error, issueId, onChange, orgId, projectId, value],
  );

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) void performUpload(file);
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          void performUpload(file);
          break;
        }
      }
    }
  };

  return (
    <div
      ref={dropRef}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      onPaste={handlePaste}
      className={cn('md-editor-themed relative overflow-hidden rounded-lg border', className)}
      data-color-mode={colorMode}
    >
      <MDEditor
        value={value}
        onChange={(val) => onChange(val ?? '')}
        preview="edit"
        height={minHeight}
        textareaProps={{ placeholder }}
        // Drop the library's own border/shadow — our wrapper provides them.
        style={{ border: 'none', boxShadow: 'none' }}
      />
      {uploading ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 rounded-b-md bg-background/95 px-3 py-2 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          <span>Uploading attachment… {progress}%</span>
          <div
            className="ml-auto h-1 w-32 overflow-hidden rounded bg-muted"
            role="progressbar"
            aria-valuenow={progress}
          >
            <div
              className="h-full bg-primary transition-[width]"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
